import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import './ObjectList.css';
import { LanguageContext } from "../context/LanguageContext";

const ObjectList = () => {
  const [objects, setObjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { language, texts } = useContext(LanguageContext);

  useEffect(() => {
    const fetchObjects = async () => {
      try {
        const token = localStorage.getItem("authToken");
        if (!token) {
          console.error("No token found.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/objects?lang=${language}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            }
          }
        );

        if (!response.ok) {
          throw new Error(`Error fetching objects: ${response.status}`);
        }

        const data = await response.json();
        setObjects(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
        setObjects([]);
      } finally {
        setLoading(false);
      }
    };

    fetchObjects();
  }, [language]);

  const handleEditPrices = async (objectId) => {
    const token = localStorage.getItem("authToken");

    try {
      const seasonsRes = await fetch(
        `${process.env.REACT_APP_API_URL}/object/${objectId}/seasons`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }
      );

      let hasSeasons = false;
      if (seasonsRes.ok) {
        const seasons = await seasonsRes.json();
        hasSeasons = Array.isArray(seasons) && seasons.length > 0;
      }

      const costsRes = await fetch(
        `${process.env.REACT_APP_API_URL}/object/${objectId}/additional-costs`,
        {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          }
        }
      );

      let hasCosts = false;
      if (costsRes.ok) {
        const costs = await costsRes.json();
        hasCosts = Array.isArray(costs) && costs.length > 0;
      }

      if (!hasSeasons && !hasCosts) {
        navigate(`/prices/${objectId}?tab=waehrung`);
      } else {
        navigate(`/prices/${objectId}`);
      }

    } catch (err) {
      console.error("Error checking price data:", err);
      navigate(`/prices/${objectId}`);
    }
  };

  const handleEditAvailability = (objectId) => navigate(`/availability/${objectId}`);

  const isConfirmed = (obj) => {
    if (typeof obj.approved !== 'undefined') return !!obj.approved;
    if (typeof obj.confirmed !== 'undefined') return !!obj.confirmed;
    if (typeof obj.is_active !== 'undefined') return !!obj.is_active;
    if (typeof obj.status !== 'undefined')
      return obj.status === 'active' || obj.status === 'confirmed';
    return true;
  };

  const sortedObjects = [...objects].sort((a, b) => {
    const aConfirmed = isConfirmed(a) ? 1 : 0;
    const bConfirmed = isConfirmed(b) ? 1 : 0;
    if (aConfirmed !== bConfirmed) return bConfirmed - aConfirmed;
    if (a.name && b.name) return a.name.localeCompare(b.name);
    return (b.created_at || 0) - (a.created_at || 0);
  });

  return (
    <div className="object-list-container">
      <h2>{texts.objectList.title}</h2>

      {loading ? (
        <p>{texts.objectList.loading}</p>
      ) : (
        <>
          {objects.length > 0 ? (
            <div className="object-list-grid">
              {sortedObjects.map((obj) => {
                const confirmed = isConfirmed(obj);
                return (
                  <div key={obj.id} className="object-card">
                    <div className="object-image-wrap">
                      <img
                        src={
                          obj.title_image
                            ? `${process.env.REACT_APP_IMAGE_BASE_URL}${obj.title_image}`
                            : "/placeholder.jpg"
                        }
                        alt={obj.name || texts.objectList.imageAlt}
                        className="object-image"
                      />
                      {!confirmed && (
                        <span className="badge-unconfirmed">
                          {texts.objectList.notConfirmed}
                        </span>
                      )}
                    </div>

                    <div className="object-details">
                      <h3 className="object-name">{obj.name}</h3>
                      <p className="object-address">
                        {obj.street} {obj.house_number}, {obj.postal_code}{" "}
                        {obj.city || obj.town}
                      </p>

                      <div className="button-group">
                        <button
                          className="edit-button"
                          onClick={() => handleEditPrices(obj.id)}
                        >
                          {texts.objectList.editPrices}
                        </button>
                        <button
                          className="availability-button"
                          onClick={() => handleEditAvailability(obj.id)}
                        >
                          {texts.objectList.editAvailability}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="no-objects-wrapper">
              <div className="no-objects-container">
                <p className="no-objects-text">
                  {texts.objectList.noObjectsLine1}<br />
                  {texts.objectList.noObjectsLine2}
                </p>

                <button
                  className="create-object-button"
                  onClick={() => navigate("/create-object")}
                >
                  {texts.objectList.createObject}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ObjectList;
